import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
	DsaiIngestionProgressStatus,
	DsaiIngestionStageState,
} from '@dealstack/shared/lib/generated';
import type { BatchParams } from '@dealstack/frontend/app/services/dealstackAi/dealstackAi.types';
import { isPollingStalled } from '@dealstack/frontend/shared/dataExchange/reactQuery/pollingRefetchInterval';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { batchQueryOptions } from '../../AnalysisWorkspace/hooks/useDealstackAiQueries';
import { canDeleteBatch } from '../../BatchDeletion/batchDeletion.utils';
import { useDeleteBatch } from '../../BatchDeletion/useDeleteBatch';
import {
	isProgressSettled,
	useCancelIngestionMutation,
	useDeleteIngestionFilesNowMutation,
	useIngestionProgressQuery,
	useRenameIngestionBatchMutation,
} from '../hooks/useIngestionQueries';
import type { UseBatchProcessingResult } from './useBatchProcessing.types';
import { toProcessingCardContent } from './useBatchProcessing.utils';
import { useClock } from './useClock';

/**
 * Polls a batch's progress while it processes and shapes it for `<ProcessingCard>`, with its cancel, rename, "Delete
 * now" and a failed batch's "Delete batch". Once processing has ended, the batch record says whether its files are due
 * for deletion and whether it has anything to review.
 */
export const useBatchProcessing = (params: BatchParams): UseBatchProcessingResult => {
	const tCapManage = useTranslationCapManage();
	const [titleDraft, setTitleDraft] = useState<string | null>(null);

	const progressQuery = useIngestionProgressQuery(params, params.batchId !== '');
	const progress = progressQuery.data;
	const isSettled = isProgressSettled(progress);
	const batchQuery = useQuery({ ...batchQueryOptions(params), enabled: isSettled });

	const cancelMutation = useCancelIngestionMutation(params);
	const renameMutation = useRenameIngestionBatchMutation(params);
	const deleteNowMutation = useDeleteIngestionFilesNowMutation(params);
	const batchDeletion = useDeleteBatch(params);

	const batch = batchQuery.data;
	const filesDeleteOn = batch?.closedAt ? batch.filesDeleteOn : null;
	const filesDeleted = deleteNowMutation.isSuccess;
	const isStalled = isPollingStalled(progressQuery);
	const now = useClock(progress?.readerWarmUp === DsaiIngestionStageState.RUNNING);
	const card = useMemo(
		() => toProcessingCardContent({ progress, isStalled, filesDeleteOn, filesDeleted, now }),
		[progress, isStalled, filesDeleteOn, filesDeleted, now]
	);

	const status = progress?.status;
	const savedTitle = progress?.title ?? '';
	const displayName = progress?.title ?? tCapManage('dealstack_ai.ingest.untitled_batch');

	const toastError = (key: string) => showToastV2({ state: 'error', title: tCapManage(key) });

	return {
		displayName,
		isComplete: status === DsaiIngestionProgressStatus.COMPLETE,
		reviewBatchId: progress?.firstBatchId ?? params.batchId,
		card,
		batchTitle: {
			value: titleDraft ?? savedTitle,
			onChange: setTitleDraft,
			onBlur: () => {
				if (titleDraft === null || titleDraft.trim() === savedTitle) {
					setTitleDraft(null);
					return;
				}
				renameMutation.mutate(titleDraft, {
					onSuccess: () => setTitleDraft(null),
					onError: () => toastError('dealstack_ai.ingest.processing.toast.rename_failed'),
				});
			},
		},
		cancel: (onCancelled) =>
			cancelMutation.mutate(undefined, {
				onSuccess: () => onCancelled?.(),
				onError: () => toastError('dealstack_ai.ingest.processing.toast.cancel_failed'),
			}),
		isCancelling: cancelMutation.isPending,
		canDeleteNow: filesDeleteOn !== null && !filesDeleted,
		deleteNow: () =>
			deleteNowMutation.mutate(undefined, {
				onSuccess: (purged) => {
					if (purged === undefined) {
						return;
					}
					const { complete, counts } = purged;
					showToastV2({
						state: complete ? 'success' : 'warning',
						title: tCapManage(
							complete
								? 'dealstack_ai.ingest.processing.toast.deleted_title'
								: 'dealstack_ai.ingest.processing.toast.deleted_partly_title'
						),
						message: tCapManage(
							'dealstack_ai.ingest.processing.toast.deleted_message',
							{ count: counts.filesPurged }
						),
					});
				},
				onError: () => toastError('dealstack_ai.ingest.processing.toast.delete_failed'),
			}),
		isDeletingNow: deleteNowMutation.isPending,
		canDeleteBatch:
			status === DsaiIngestionProgressStatus.ERROR &&
			batch !== undefined &&
			canDeleteBatch(batch),
		deleteBatch: (onDeleted) =>
			batchDeletion.deleteBatch({ kind: 'failed', name: displayName, onDeleted }),
		isDeletingBatch: batchDeletion.isDeleting,
		notifyWhenComplete: () =>
			showToastV2({
				title: tCapManage('dealstack_ai.ingest.processing.toast.notify_title'),
				message: tCapManage('dealstack_ai.ingest.processing.toast.notify_message', {
					name: displayName,
				}),
			}),
	};
};
